import axios from 'axios'
import config from '@/config'
import { store } from '@/store'

const baseURL = config.baseURL

/**
 * Session-based axios instance
 * - Uses HttpOnly cookie for authentication (handled automatically by browser)
 * - No token storage in JavaScript (XSS protection)
 */
const axiosInstance = axios.create({
  baseURL,
  timeout: 30000,
  withCredentials: true, // Send cookies with requests
})

/**
 * Request Interceptor
 * - No token handling needed (cookie is sent automatically)
 */
axiosInstance.interceptors.request.use(
  function (config) {
    return config
  },
  function (error) {
    return Promise.reject(error)
  }
)

/**
 * Response Interceptor
 * - 401: Session expired, redirect to login
 * - 403: Permission denied
 * - 422: Validation error
 * - Other errors
 */
axiosInstance.interceptors.response.use(
  function (response) {
    // Success - clear validation errors
    store.commit('error/setValidationError', {})
    return response
  },
  async function (error) {
    // Network error or no response
    if (!error.response) {
      store.commit('error/setNetworkError', {
        message: 'Network error. Please check your connection.',
        code: 'NETWORK_ERROR',
      })
      return Promise.reject(error)
    }

    const { status, data } = error.response

    // 401 Unauthorized - Session expired
    if (status === 401) {
      store.commit('auth/setLoginStatus', false)
      store.commit('auth/clearGroupsRolesAndPermissions')
      // Don't redirect here, App.vue will show login UI
      return Promise.reject(error)
    }

    // 403 Forbidden - Permission denied
    if (status === 403) {
      const msg = 'You do not have permission to perform this action.'
      store.commit('error/setPermissionError', {
        message: data?.message || msg,
        code: 'PERMISSION_DENIED',
      })
      return Promise.reject(error)
    }

    // 422 Unprocessable Entity - Validation error
    if (status === 422) {
      store.commit('error/setValidationError', data?.data || {})
      return Promise.reject(error)
    }

    // 500 Internal Server Error
    if (status >= 500) {
      store.commit('error/setNetworkError', {
        message: 'Server error. Please try again later.',
        code: 'SERVER_ERROR',
      })
      return Promise.reject(error)
    }

    // Other errors
    return Promise.reject(error)
  }
)

/**
 * Check session status from server
 * @returns {Promise<Object|null>} Session data if logged in, null otherwise
 */
export const checkSession = async () => {
  try {
    const response = await axiosInstance.get('/session')
    if (response.data?.loggedIn) {
      return {
        address: response.data.address,
        groups: response.data.groups || [],
        roles: response.data.roles || [],
        permissions: response.data.permissions || [],
      }
    }
    return null
  } catch (error) {
    return null
  }
}

/**
 * Logout - Clear session on server
 * @returns {Promise<boolean>} true if successful
 */
export const logout = async () => {
  try {
    await axiosInstance.post('/signOut')
    return true
  } catch (error) {
    return false
  }
}

export default axiosInstance
